import { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import "./SchoolPerformance.css";

/**
 * Two performance sentences for one school.
 * Remove this file, SchoolPerformance.css, schoolPerformanceService,
 * the /performance route, and the Performance section in SchoolManage to take it out.
 */

type Performance = {
  students: number;
  exams: { met: number; scored: number; leftOut: number };
  minutes: { met: number; withPlan: number };
  courses: { met: number; withWork: number };
};

function shareLine(met: number, total: number, rest: string) {
  const pct = total ? Math.round((met / total) * 100) : 0;
  return `${met} of ${total}, or ${pct}%, of students ${rest}`;
}

function countLine(met: number, total: number, rest: string) {
  const one = met === 1;
  if (rest.startsWith("have")) {
    const verb = one ? "student has" : "students have";
    return `${met} of ${total} ${verb} ${rest.replace(/^have /, "")}`;
  }
  const verb = one ? "student is" : "students are";
  return `${met} of ${total} ${verb} ${rest}`;
}

export default function SchoolPerformance({ schoolId }: { schoolId: string }) {
  const [data, setData] = useState<Performance | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data?: Performance }>(
      `/api/schools/${encodeURIComponent(schoolId)}/performance`,
    )
      .then((envelope) => {
        if (!cancel) setData(envelope.data || null);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [schoolId]);

  if (error) return <p className="al-school-performance-note">{error}</p>;
  if (!data) return <p className="al-school-performance-note">Loading performance…</p>;
  if (!data.students) {
    return <p className="al-school-performance-note">No students are enrolled in this school's courses.</p>;
  }

  const examLine =
    data.exams.scored > 0
      ? countLine(data.exams.met, data.exams.scored, "at or above 85% on exams.")
      : "No exam scores yet.";
  const examAside =
    data.exams.leftOut > 0
      ? `${data.exams.leftOut} ${data.exams.leftOut === 1 ? "student has" : "students have"} no exam score yet.`
      : "";
  const minuteLine =
    data.minutes.withPlan > 0
      ? countLine(
          data.minutes.met,
          data.minutes.withPlan,
          "have finished at least two thirds of the minutes in their courses.",
        )
      : "No courses at this school have minutes planned yet.";
  const courseLine =
    data.courses.withWork > 0
      ? shareLine(
          data.courses.met,
          data.courses.withWork,
          "have finished at least 20% of their courses.",
        )
      : "";

  return (
    <div className="al-school-performance">
      <p className="al-school-performance-line">{examLine}</p>
      {examAside ? <p className="al-school-performance-note">{examAside}</p> : null}
      <p className="al-school-performance-line al-school-performance-line--next">{minuteLine}</p>
      {courseLine ? <p className="al-school-performance-line al-school-performance-line--next">{courseLine}</p> : null}
    </div>
  );
}
